/*
 * Matthews search panel.
 * - Instant results from Shopify's predictive search (no app).
 * - Understands prices ("under 8k"), misspellings and other names, and can
 *   optionally turn words into the existing storefront filters (read from
 *   sections/mj-filter-map.liquid). The filters themselves are never changed.
 * - Editable word lists and page shortcuts live in sections/mj-search.liquid.
 */
(() => {
  if (window.MJSearch) return;

  const configEl = document.getElementById('mj-search-config');
  const root = document.getElementById('mj-search');
  if (!configEl || !root) return;

  let CFG;
  try {
    CFG = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  // Keep the panel out of the header's stacking context.
  document.body.appendChild(root);

  const input = root.querySelector('.mj-search__input');
  const body = root.querySelector('.mj-search__body');
  const clearBtn = root.querySelector('.mj-search__clear');
  const ROOT = (CFG.root || '/').replace(/\/$/, '') + '/';
  const RECENT_KEY = 'mj-recent-searches';
  // Off until product filter data is complete (see the section setting).
  const FILTERS_ON = CFG.smartFilters === true;
  // Pages kept out of the panel (handles or links, from the section setting).
  const HIDDEN_PAGES = String(CFG.hidePages || '')
    .split(/[\s,]+/)
    .map((h) => h.trim().replace(/^.*\/pages\//, '').replace(/[/?#].*$/, ''))
    .filter(Boolean);
  const isHiddenPage = (url) => {
    const path = String(url || '').split('?')[0].replace(/\/$/, '');
    return HIDDEN_PAGES.some((h) => path.endsWith('/pages/' + h));
  };

  /* ---------- text helpers ---------- */

  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/[’‘`]/g, "'")
      .replace(/[^a-z0-9$.,'&+\- ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const splitTerms = (s) =>
    String(s || '')
      .split(/[,\n]/)
      .map(norm)
      .filter(Boolean);

  const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const wordRe = (phrase) => new RegExp('(^|\\s)' + escRe(phrase) + '(?=\\s|$)', 'g');

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // Bold the words the customer typed, e.g. "toi" -> <mark>toi</mark> et moi.
  const highlight = (text, q) => {
    const words = norm(q).split(' ').filter((w) => w.length > 0).sort((a, b) => b.length - a.length);
    if (!words.length) return esc(text);
    const re = new RegExp('(' + words.map(escRe).join('|') + ')', 'gi');
    return String(text)
      .split(re)
      .map((part, i) => (i % 2 ? '<mark>' + esc(part) + '</mark>' : esc(part)))
      .join('');
  };

  const money = (amount) => {
    const n = Number(amount);
    if (!isFinite(n)) return '';
    return '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + (CFG.currency ? ' ' + CFG.currency : '');
  };

  const sizedImage = (src, width) => {
    if (!src) return '';
    try {
      const u = new URL(src, location.origin);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (e) {
      return src;
    }
  };

  /* ---------- editable word lists ---------- */

  const SYNONYMS = (CFG.synonyms || [])
    .flatMap((s) => splitTerms(s.terms).map((t) => ({ from: t, to: norm(s.means) })))
    .filter((s) => s.from && s.to)
    .sort((a, b) => b.from.length - a.from.length);

  const RULES = (CFG.rules || [])
    .map((r) => ({ terms: splitTerms(r.terms), url: (r.url || '').trim(), label: (r.label || '').trim() }))
    .filter((r) => r.terms.length && r.url);

  const applySynonyms = (q) => {
    let out = ' ' + norm(q) + ' ';
    SYNONYMS.forEach(({ from, to }) => {
      out = out.replace(wordRe(from), (m, pre) => pre + to);
    });
    return out.replace(/\s+/g, ' ').trim();
  };

  const findRule = (q) => {
    const n = norm(q);
    const s = applySynonyms(q);
    return RULES.find((r) => r.terms.includes(n) || r.terms.includes(s)) || null;
  };

  // Shortcuts to show while typing (prefix match on any rule term).
  const ruleHints = (q) => {
    const n = norm(q);
    if (n.length < 3) return [];
    return RULES.filter((r) => r.terms.some((t) => t.startsWith(n) || n.startsWith(t))).slice(0, 3);
  };

  /* ---------- filter vocabulary (read from the live filters) ---------- */

  // Extra ways customers say a filter value. Keys are filter value labels.
  const ALIASES = {
    'lab grown diamonds': ['lab grown diamonds', 'lab grown diamond', 'lab grown', 'lab diamonds', 'lab diamond', 'lab created', 'lgd'],
    'natural diamonds': ['natural diamonds', 'natural diamond', 'mined diamond', 'earth mined'],
    'natural sapphires': ['natural sapphires', 'natural sapphire', 'sapphires', 'sapphire', 'australian sapphire'],
    emerald: ['emerald gemstone', 'green emerald'],
    '18k yellow gold': ['18k yellow gold', 'yellow gold'],
    'white gold': ['18k white gold', 'white gold'],
    'rose gold': ['18k rose gold', 'rose gold'],
    'sterling silver': ['sterling silver', 'sterling'],
    pearls: ['pearls', 'pearl'],
    oval: ['oval cut', 'ovals', 'oval'],
    round: ['round brilliant', 'round cut', 'round'],
    pear: ['pear shape', 'pear cut', 'pears', 'pear'],
    square: ['princess cut', 'princess', 'square'],
    cushion: ['elongated cushion', 'cushion cut', 'cushion'],
    marquise: ['marquise cut', 'marquise'],
    heart: ['heart shape', 'heart shaped', 'heart'],
    'three stone': ['three stone', 'trilogy', '3 stone'],
    'toi et moi': ['toi et moi', 'you and me'],
    'hidden halo': ['hidden halo'],
  };
  // Values too broad to guess from a single word.
  const NEVER = ['diamond', 'gold', 'metal', 'precious/semiprecious stones', 'rectangular', 'silver plated'];

  let vocabPromise = null;
  let vocab = []; // { alias, param, value, label, group }
  let priceParams = { min: 'filter.v.price.gte', max: 'filter.v.price.lte' };

  const loadVocab = () => {
    if (vocabPromise) return vocabPromise;
    if (!FILTERS_ON) return (vocabPromise = Promise.resolve(vocab));
    const cacheKey = 'mj-filter-map-v1';
    vocabPromise = (async () => {
      let map = null;
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (cached && Date.now() - cached.t < 6 * 3600 * 1000) map = cached.map;
      } catch (e) {}
      if (!map) {
        const res = await fetch(ROOT + 'collections/all?section_id=mj-filter-map');
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.querySelector('[data-mj-filter-map]');
        map = el ? JSON.parse(el.textContent) : [];
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), map }));
        } catch (e) {}
      }
      const list = [];
      map.forEach((f) => {
        if (f.type === 'price_range') {
          priceParams = { min: f.min || priceParams.min, max: f.max || priceParams.max };
          return;
        }
        (f.values || []).forEach((v) => {
          const key = norm(v.label);
          if (!key || NEVER.includes(key)) return;
          const aliases = ALIASES[key] || [key];
          aliases.forEach((a) => list.push({ alias: norm(a), param: f.param, value: v.value, label: v.label, group: f.label }));
        });
      });
      list.sort((a, b) => b.alias.length - a.alias.length);
      vocab = list;
      return vocab;
    })().catch(() => (vocab = []));
    return vocabPromise;
  };

  /* ---------- understanding a search ---------- */

  const toNumber = (num, k) => {
    const n = parseFloat(String(num).replace(/,/g, ''));
    if (!isFinite(n)) return null;
    return Math.round(k ? n * 1000 : n);
  };

  const parsePrice = (text) => {
    // A product code like "1-9779" or "2-5322" is a SKU, not a price range.
    if (/^\s*\d{1,3}-\d{3,}\s*$/.test(text)) return { min: null, max: null, rest: String(text).trim() };
    let t = ' ' + text + ' ';
    let min = null;
    let max = null;
    const NUM = '\\$?\\s?(\\d[\\d,]*(?:\\.\\d+)?)\\s?(k)?';
    const range = new RegExp('(?:between\\s+)?' + NUM + '\\s?(?:-|to|and)\\s?' + NUM + '(?=\\s)', 'i');
    const under = new RegExp('(?:under|below|less than|up to|max|maximum|<)\\s?' + NUM + '(?=\\s)', 'i');
    const over = new RegExp('(?:over|above|more than|from|min|minimum|>)\\s?' + NUM + '(?=\\s)', 'i');
    const big = (n) => n != null && n >= 100;
    let m = t.match(range);
    if (m && big(toNumber(m[3], m[4]))) {
      min = toNumber(m[1], m[2] || m[4]);
      max = toNumber(m[3], m[4]);
      t = t.replace(m[0], ' ');
    } else {
      m = t.match(under);
      if (m && big(toNumber(m[1], m[2]))) {
        max = toNumber(m[1], m[2]);
        t = t.replace(m[0], ' ');
      }
      m = t.match(over);
      if (m && big(toNumber(m[1], m[2]))) {
        min = toNumber(m[1], m[2]);
        t = t.replace(m[0], ' ');
      }
    }
    if (min != null && min < 100) min = null;
    return { min, max, rest: t.replace(/\s+/g, ' ').trim() };
  };

  const understand = (q) => {
    const text = applySynonyms(q);
    const price = parsePrice(text);
    const query = price.rest;
    let rest = ' ' + query + ' ';
    const filters = [];
    const usedParams = new Set();
    (FILTERS_ON ? vocab : []).forEach((v) => {
      if (usedParams.has(v.param + '|' + v.value)) return;
      const re = wordRe(v.alias);
      if (re.test(rest)) {
        filters.push(v);
        usedParams.add(v.param + '|' + v.value);
        rest = rest.replace(wordRe(v.alias), ' ');
      }
    });
    rest = rest
      .replace(/(^|\s)(in|with|and|a|an|the|for)(?=\s|$)/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return { text, query, filters, min: price.min, max: price.max, rest };
  };

  const plainSearchUrl = (q) => {
    const p = new URLSearchParams();
    p.set('q', q);
    p.set('options[unavailable_products]', 'last');
    p.set('options[prefix]', 'last');
    return CFG.searchUrl + '?' + p.toString();
  };

  const smartUrl = (u) => {
    const p = new URLSearchParams();
    let base;
    // Words that aren't filters ("2ct", "engagement ring") stay as the text search.
    const text = FILTERS_ON ? u.rest : u.query;
    if (text) {
      base = CFG.searchUrl;
      p.set('q', text);
      p.set('type', 'product');
      p.set('options[unavailable_products]', 'last');
      p.set('options[prefix]', 'last');
    } else {
      base = CFG.allUrl || ROOT + 'collections/all';
    }
    u.filters.forEach((f) => p.append(f.param, f.value));
    if (u.min != null) p.set(priceParams.min, u.min);
    if (u.max != null) p.set(priceParams.max, u.max);
    return base + '?' + p.toString();
  };

  // Use the filters automatically for a price, or for two or more filter words.
  const isSmart = (u) => u.min != null || u.max != null || u.filters.length >= 2;

  // Where Enter should take the customer.
  const destination = async (q) => {
    const raw = (q || '').trim();
    if (!raw) return null;
    const rule = findRule(raw);
    if (rule) return rule.url;
    await loadVocab();
    const u = understand(raw);
    if (isSmart(u)) return smartUrl(u);
    return plainSearchUrl(u.text || raw);
  };

  /* ---------- analytics ---------- */

  const track = (name, params) => {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
      else (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, params));
    } catch (e) {}
  };

  /* ---------- recent searches ---------- */

  const getRecent = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (e) {
      return [];
    }
  };
  const saveRecent = (q) => {
    const t = (q || '').trim();
    if (!t) return;
    try {
      const list = [t].concat(getRecent().filter((x) => x.toLowerCase() !== t.toLowerCase())).slice(0, 5);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) {}
  };

  /* ---------- data fetching ---------- */

  let controller = null;
  const predictive = async (q) => {
    if (controller) controller.abort();
    controller = new AbortController();
    const p = new URLSearchParams();
    p.set('q', q);
    p.set('resources[type]', 'product,query,collection,page,article');
    p.set('resources[limit]', '10');
    p.set('resources[limit_scope]', 'each');
    p.set('resources[options][unavailable_products]', 'last');
    p.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,tag,vendor');
    p.set('section_id', 'mj-predictive');
    const res = await fetch(ROOT + 'search/suggest?' + p.toString(), { signal: controller.signal });
    const html = await res.text();
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mj-predictive]');
    return el ? JSON.parse(el.textContent) : {};
  };

  const fromPredictive = (p) => ({
    title: p.title,
    url: p.url,
    image: p.image,
    image2: p.image2,
    price: p.price != null ? p.price / 100 : null,
    from: p.priceVaries,
    // MJ (31/10/2026): was price and sale flag from sections/mj-predictive
    was: p.compareAt ? p.compareAt / 100 : null,
    onSale: !!p.onSale,
    available: p.available,
    subtitle: p.subtitle,
    tags: p.badge ? ['badge:' + p.badge] : [],
    type: p.type,
  });

  // MJ (31/10/2026): /products/x.js prices are in cents. The was price is the smallest compare-at among on-sale
  // variants at the shown (lowest) price.
  const wasFromProductJs = (p) => {
    const vs = p.variants || [];
    const cmps = vs
      .filter((v) => v.price === p.price && v.compare_at_price && v.compare_at_price > v.price)
      .map((v) => v.compare_at_price);
    return cmps.length ? Math.min.apply(null, cmps) / 100 : null;
  };

  const fromProductJs = (p) => ({
    title: p.title,
    url: p.url || ROOT + 'products/' + p.handle,
    image: p.featured_image,
    image2: p.images && p.images[1] ? p.images[1] : null,
    price: (p.price || 0) / 100,
    from: p.price_varies,
    was: wasFromProductJs(p),
    onSale: (p.variants || []).some((v) => v.compare_at_price && v.compare_at_price > v.price),
    available: p.available,
    tags: p.tags || [],
    type: p.type,
  });

  // MJ (31/10/2026): /products.json prices are dollar strings; compare_at_price can be null
  const fromProductsJson = (p) => {
    const v0 = p.variants && p.variants[0] ? p.variants[0] : null;
    return {
      title: p.title,
      url: ROOT + 'products/' + p.handle,
      image: p.images && p.images[0] ? p.images[0].src : '',
      image2: p.images && p.images[1] ? p.images[1].src : null,
      price: v0 ? v0.price : null,
      was: v0 && v0.compare_at_price != null && Number(v0.compare_at_price) > Number(v0.price) ? Number(v0.compare_at_price) : null,
      onSale: (p.variants || []).some((v) => v.compare_at_price != null && Number(v.compare_at_price) > Number(v.price)),
      available: (p.variants || []).some((v) => v.available),
      tags: p.tags || [],
      type: p.product_type,
    };
  };

  const isGiftCard = (p) => /gift card/i.test(p.type || '') || /gift-card/.test(p.url || '');

  let startProducts = null;
  const loadStartProducts = async () => {
    if (startProducts) return startProducts;
    let list = [];
    try {
      const handles = (localStorage.getItem('recently-viewed') || '').split(',').filter(Boolean).slice(0, 8);
      if (handles.length) {
        const results = await Promise.all(
          handles.map((h) =>
            fetch(ROOT + 'products/' + h + '.js')
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        );
        list = results.filter(Boolean).map(fromProductJs);
        if (list.length) startProducts = { heading: 'Recently viewed', items: list };
      }
    } catch (e) {}
    if (!startProducts && CFG.fallbackCollection) {
      try {
        const r = await fetch(ROOT + 'collections/' + encodeURIComponent(CFG.fallbackCollection) + '/products.json?limit=8');
        const d = await r.json();
        list = (d.products || []).map(fromProductsJson);
        startProducts = { heading: 'Most loved', items: list };
      } catch (e) {}
    }
    return startProducts || { heading: '', items: [] };
  };

  /* ---------- rendering ---------- */

  // MJ (31/10/2026): returns { text, sale }. Sold out still wins. A product on sale shows its tag badge when that
  // already says sale (e.g. "New to Sale"), otherwise "Sale"; sale badges get the sale colour.
  const badgeFor = (p) => {
    const tag = (p.tags || []).find((t) => /^badge:/i.test(t));
    const tagText = tag ? tag.replace(/^badge:/i, '').trim() : '';
    if (p.available !== false && p.onSale) {
      if (tagText && /sale/i.test(tagText)) return { text: tagText, sale: true };
      return { text: 'Sale', sale: true };
    }
    if (tagText) return { text: tagText, sale: false };
    if (p.available === false) return { text: 'Sold out', sale: false };
    return { text: '', sale: false };
  };

  const card = (p, i, q) => {
    const badge = badgeFor(p);
    const img = sizedImage(p.image, 500);
    const img2 = p.image2 ? sizedImage(p.image2, 500) : '';
    // MJ (31/10/2026): was / now price when on sale
    const onSalePrice = p.price != null && p.was != null && Number(p.was) > Number(p.price);
    return (
      '<a class="mj-card' + (img2 ? ' mj-card--hover' : '') + '" href="' + esc(p.url) + '" data-index="' + i + '" data-title="' + esc(p.title) + '">' +
      '<div class="mj-card__img">' +
      (img ? '<img src="' + esc(img) + '" alt="' + esc(p.title) + '" loading="lazy" width="500" height="500">' : '') +
      (img2 ? '<img class="mj-card__img2" src="' + esc(img2) + '" alt="" loading="lazy" width="500" height="500" aria-hidden="true">' : '') +
      (badge.text ? '<span class="mj-card__badge' + (badge.sale ? ' mj-card__badge--sale' : '') + '">' + esc(badge.text) + '</span>' : '') +
      '</div>' +
      '<p class="mj-card__title">' + esc(p.title) + '</p>' +
      (p.subtitle ? '<p class="mj-card__subtitle">' + esc(p.subtitle) + '</p>' : '') +
      (onSalePrice
        ? '<p class="mj-card__price">' + (p.from ? 'From ' : '') + '<del class="price__compare">' + esc(money(p.was)) + '</del> <ins class="price__sale">' + esc(money(p.price)) + '</ins></p>'
        : p.price != null ? '<p class="mj-card__price">' + (p.from ? 'From ' : '') + esc(money(p.price)) + '</p>' : '') +
      '</a>'
    );
  };

  const chip = (label, attrs) => '<button type="button" class="mj-chip" ' + attrs + '>' + esc(label) + '</button>';

  const ctaBlock = (q) =>
    '<div class="mj-cta">' +
    (q ? '<p class="mj-cta__lead">No exact matches for “' + esc(q) + '”.</p>' : '') +
    '<p class="mj-cta__heading">' + esc(CFG.cta.heading) + '</p>' +
    '<p class="mj-cta__text">' + esc(CFG.cta.text) + '</p>' +
    '<a class="mj-cta__btn" href="' + esc(CFG.cta.url) + '">' + esc(CFG.cta.label) + '</a>' +
    '</div>';

  const renderStart = async () => {
    root.classList.add('is-empty');
    const recent = getRecent();
    const side =
      (CFG.popular.length
        ? '<section><h2 class="mj-search__h">Popular searches</h2><div class="mj-chips">' +
          CFG.popular.map((t) => chip(t, 'data-q="' + esc(t) + '"')).join('') +
          '</div></section>'
        : '') +
      (recent.length
        ? '<section><h2 class="mj-search__h">Your recent searches</h2><ul class="mj-list">' +
          recent.map((t) => '<li><button type="button" data-q="' + esc(t) + '">' + esc(t) + '</button></li>').join('') +
          '</ul></section>'
        : '');
    body.innerHTML = '<aside class="mj-search__side">' + side + '</aside><div class="mj-search__main"></div>';
    const start = await loadStartProducts();
    if (input.value.trim()) return; // customer started typing meanwhile
    const main = body.querySelector('.mj-search__main');
    if (main && start.items.length) {
      main.innerHTML =
        '<div class="mj-search__head"><h2 class="mj-search__h">' + esc(start.heading) + '</h2></div>' +
        '<div class="mj-grid">' + start.items.filter((p) => !isGiftCard(p)).slice(0, 8).map((p, i) => card(p, i, '')).join('') + '</div>';
    }
  };

  let lastNoResult = '';
  let renderToken = 0;

  const renderResults = async (q) => {
    const token = ++renderToken;
    root.classList.remove('is-empty');
    root.classList.add('is-loading');
    await loadVocab();
    const u = understand(q);
    const searchText = u.query || u.text || q;
    let r = {};
    try {
      r = await predictive(searchText);
    } catch (e) {
      if (e.name === 'AbortError') return;
    }
    if (token !== renderToken) return;
    root.classList.remove('is-loading');

    let products = (r.products || [])
      .map(fromPredictive)
      .filter((p) => !isGiftCard(p))
      .filter((p) => (u.max == null || Number(p.price) <= u.max) && (u.min == null || Number(p.price) >= u.min));
    const queries = (r.queries || []).map((x) => x.text).filter((t) => norm(t) !== norm(q)).slice(0, 5);
    const links = []
      .concat(ruleHints(q).map((x) => ({ title: x.label || x.terms[0], url: x.url })))
      .concat((r.collections || []).map((c) => ({ title: c.title, url: c.url })))
      .filter((l, i, a) => a.findIndex((x) => x.url.split('?')[0] === l.url.split('?')[0] || x.title.toLowerCase() === l.title.toLowerCase()) === i)
      .slice(0, 6);
    // Guides and journal posts (policy and competition pages are left out).
    const guides = []
      .concat(r.pages || [], r.articles || [])
      .filter((g) => !/terms|t ?& ?c|policy|giveaway|conditions/i.test(g.title))
      .filter((g) => !isHiddenPage(g.url))
      .filter((g) => !links.some((l) => l.url.split('?')[0] === g.url.split('?')[0]))
      .slice(0, 4);

    const smart = u.filters.length || u.min != null || u.max != null;
    const allUrl = isSmart(u) ? smartUrl(u) : plainSearchUrl(searchText);
    const smartBlock = smart
      ? '<section class="mj-smart"><h2 class="mj-search__h">Matching your search</h2><div class="mj-chips mj-chips--static">' +
        u.filters.map((f) => '<span class="mj-chip mj-chip--static">' + esc(f.label) + '</span>').join('') +
        (u.min != null ? '<span class="mj-chip mj-chip--static">From $' + u.min.toLocaleString('en-AU') + '</span>' : '') +
        (u.max != null ? '<span class="mj-chip mj-chip--static">Up to $' + u.max.toLocaleString('en-AU') + '</span>' : '') +
        ((FILTERS_ON ? u.rest : u.query) ? '<span class="mj-chip mj-chip--static">“' + esc(FILTERS_ON ? u.rest : u.query) + '”</span>' : '') +
        '</div><a class="mj-link" href="' + esc(smartUrl(u)) + '" data-smart>See every matching piece →</a></section>'
      : '';

    const side =
      smartBlock +
      (links.length
        ? '<section><h2 class="mj-search__h">Go to</h2><ul class="mj-list">' +
          links.map((l) => '<li><a href="' + esc(l.url) + '">' + esc(l.title) + '</a></li>').join('') +
          '</ul></section>'
        : '') +
      (queries.length
        ? '<section><h2 class="mj-search__h">Suggestions</h2><ul class="mj-list">' +
          queries.map((t) => '<li><button type="button" data-q="' + esc(t) + '">' + highlight(t, q) + '</button></li>').join('') +
          '</ul></section>'
        : '') +
      (guides.length
        ? '<section><h2 class="mj-search__h">Guides &amp; journal</h2><ul class="mj-list mj-list--guides">' +
          guides.map((g) => '<li><a href="' + esc(g.url) + '">' + highlight(g.title, q) + '</a></li>').join('') +
          '</ul></section>'
        : '');

    let main;
    if (products.length) {
      main =
        '<div class="mj-search__head"><h2 class="mj-search__h">Products</h2>' +
        '<a class="mj-link" href="' + esc(allUrl) + '" data-viewall>View all results →</a></div>' +
        '<div class="mj-grid">' + products.slice(0, 8).map((p, i) => card(p, i, q)).join('') + '</div>';
    } else {
      main = ctaBlock(q);
      if (lastNoResult !== norm(q)) {
        lastNoResult = norm(q);
        track('mj_search_no_results', { search_term: q, location: 'search_panel' });
      }
    }

    body.innerHTML =
      (side ? '<aside class="mj-search__side">' + side + '</aside>' : '<aside class="mj-search__side"></aside>') +
      '<div class="mj-search__main">' + main + '</div>';
  };

  /* ---------- open / close / submit ---------- */

  let debounce = null;
  let lastTrigger = null;
  let savedScroll = 0;

  // The panel opens just below the site header so the logo and menu stay visible.
  const headerEl = () =>
    document.querySelector('.section--header, sticky-header, header.header') || document.querySelector('header');

  const placeBelowHeader = () => {
    const h = headerEl();
    const bottom = h ? Math.max(0, Math.round(h.getBoundingClientRect().bottom)) : 0;
    root.style.top = bottom + 'px';
  };

  const update = () => {
    const q = input.value.trim();
    clearBtn.hidden = !q;
    clearTimeout(debounce);
    if (!q) {
      renderToken++;
      renderStart();
      return;
    }
    debounce = setTimeout(() => renderResults(q), 160);
  };

  const open = (prefill) => {
    lastTrigger = document.activeElement;
    if (root.hidden) {
      savedScroll = window.scrollY || 0;
      window.scrollTo(0, 0);
    }
    placeBelowHeader();
    root.hidden = false;
    document.documentElement.classList.add('mj-search-open');
    if (typeof prefill === 'string') input.value = prefill;
    input.focus({ preventScroll: true });
    try {
      input.setSelectionRange(input.value.length, input.value.length);
    } catch (e) {}
    loadVocab();
    update();
  };

  const close = () => {
    if (root.hidden) return;
    root.hidden = true;
    document.documentElement.classList.remove('mj-search-open');
    window.scrollTo(0, savedScroll);
    // Don't refocus another search box (that would reopen the panel).
    if (lastTrigger && lastTrigger.focus && !(lastTrigger.matches && lastTrigger.matches('input'))) {
      try {
        lastTrigger.focus({ preventScroll: true });
      } catch (e) {}
    }
  };

  const go = async (q) => {
    const raw = (q || '').trim();
    if (!raw) return;
    saveRecent(raw);
    track('search', { search_term: raw });
    const url = await destination(raw);
    if (url) window.location.href = url;
  };

  input.addEventListener('input', update);
  root.querySelector('.mj-search__bar').addEventListener('submit', (e) => {
    e.preventDefault();
    go(input.value);
  });
  clearBtn.addEventListener('click', () => {
    input.value = '';
    input.focus();
    update();
  });
  root.querySelector('.mj-search__close').addEventListener('click', close);
  window.addEventListener('resize', () => {
    if (!root.hidden) placeBelowHeader();
  });
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
  root.addEventListener('click', (e) => {
    // Clicking the dimmed page below the panel closes search.
    if (e.target === root) {
      close();
      return;
    }
    const qBtn = e.target.closest('[data-q]');
    if (qBtn) {
      input.value = qBtn.getAttribute('data-q');
      go(input.value);
      return;
    }
    const cardEl = e.target.closest('.mj-card');
    if (cardEl) {
      saveRecent(input.value);
      track('select_item', {
        item_list_name: 'search_panel',
        search_term: input.value.trim(),
        items: [{ item_name: cardEl.getAttribute('data-title'), index: Number(cardEl.getAttribute('data-index')) }],
      });
      return;
    }
    if (e.target.closest('[data-viewall],[data-smart]')) saveRecent(input.value);
  });

  // Any other site search box (phone menu, results page) opens the panel too.
  const isSiteSearchInput = (el) =>
    el && el.matches && el.matches('input[name="q"]') && !root.contains(el) && el.closest('form.js-search-form, form[role="search"]') && !el.closest('facet-filters-form');

  // Open from the header magnifying glass instead of the theme's own bar.
  document.addEventListener(
    'click',
    (e) => {
      if (!e.target.closest) return;
      const field = e.target.closest('input[name="q"]');
      if (field && isSiteSearchInput(field)) {
        if (root.hidden) open(field.value);
        return;
      }
      const trigger = e.target.closest('.header__search-container > summary, [data-mj-search-open]');
      if (!trigger) {
        // Using the header (menu, logo, account) while search is open closes it.
        const h = headerEl();
        if (!root.hidden && h && h.contains(e.target)) close();
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      if (root.hidden) open();
      else close();
    },
    true
  );

  document.addEventListener(
    'focusin',
    (e) => {
      if (!isSiteSearchInput(e.target)) return;
      const v = e.target.value;
      e.target.blur();
      open(v);
    },
    true
  );

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (root.contains(form) || form.closest('facet-filters-form')) return;
      const q = form.querySelector && form.querySelector('input[name="q"]');
      if (!q || !form.matches('form.js-search-form, form[role="search"]')) return;
      e.preventDefault();
      go(q.value);
    },
    true
  );

  window.MJSearch = { open, close, understand, destination, loadVocab };
})();
